import { useContext } from "react";
import { useNavigate } from "react-router";

import { FeatureTypeDefinition } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";

import { PROCESS_SEARCH_KEY } from "@/config/query-parameter-consts";
import { getProjectTabQueryParam } from "@/features/project";
import { useProjectFiles } from "@/hooks/use-project-files";
import { useProcessByTagQueryDirectly } from "@/queries/query-process";
import { ProjectContext } from "@/stores/project-provider";

import { getDefaultFileRef } from "../process-run-form-file";
import { ProcessRunFormFileModalProps } from "../types";
import {
  getFileDestinationId,
  getInitialFileName,
  getInitialPathSegments,
} from "../utils/file-modal-state";
import { getAcceptedDataTypes } from "../utils/get-accepted-data-types";

// We use this hook to sit between the state of the actual formData and the state of the modal.
// The modal should take the formData as initial state, but not update the formData until the user clicks "Select" in the modal.
// This is to avoid the formData being updated when the user is still browsing files and hasn't made a final selection yet.
// If the user clicks "Cancel" in the modal, we don't want to update the formData at all and we derender the modal to reset the state.
export const useProcessRunFormFileModal = ({
  formData,
  onChange,
  schema,
  mode,
}: ProcessRunFormFileModalProps) => {
  const { tenant } = useContext(TenantContext);
  const { slug: projectSlug } = useContext(ProjectContext);
  const navigate = useNavigate();

  const { acceptedDataTypes, acceptedFeatureTypes } = getAcceptedDataTypes(schema);

  const initialPathSegments = getInitialPathSegments(formData?.id, mode);
  const {
    fileName: tempFileName,

    // All of these we just pass on
    paths,
    setPaths,
    path,
    setFileName,
    intl,
    files,
    tableDataIds,
    isLoading,
    onDirectoryOrFileClick,
    clearFile,
    getRootProps,
    getInputProps,
    openDropzone: open,
  } = useProjectFiles({ initialPaths: initialPathSegments });

  const fileName = getInitialFileName(formData?.id, mode, tempFileName);

  const saveChanges = () => {
    const id = getFileDestinationId(mode, path, fileName);

    onChange({
      id,
      ref: getDefaultFileRef(schema),
    });
  };

  const acceptsStudyAreaFile = acceptedFeatureTypes.includes(FeatureTypeDefinition.StudyArea);
  const processByTagQueryDirectly = useProcessByTagQueryDirectly();
  // Actually navigate instead of just changing the process id because this can also be called from the catalogue
  const goToDefineStudyArea = async () => {
    const { isSuccess, data: process } = await processByTagQueryDirectly("define_study_area");
    if (!isSuccess) return;
    navigate(
      `/${tenant}/projects/${projectSlug}?${getProjectTabQueryParam(
        "processes"
      )}&${PROCESS_SEARCH_KEY}=${process.id}`
    );
  };

  return {
    paths,
    setPaths,
    setFileName,
    fileName,
    intl,
    files,
    tableDataIds,
    isLoading,
    onDirectoryOrFileClick,
    saveChanges,
    clearFile,
    getRootProps,
    getInputProps,
    open,
    acceptedDataTypes,
    acceptedFeatureTypes,
    acceptsStudyAreaFile,
    goToDefineStudyArea,
  };
};
